import { v4 as uuid } from "uuid";
import { format } from "date-fns";
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { Nullish } from "utility-types";
import { formatOrdinals } from "./ordinal";
import moment from "moment-timezone";
import _, { isFinite } from "lodash";

const isoTimezoneRegex = /(Z|[+-](?:2[0-3]|[01][0-9])(?::?(?:[0-5][0-9]))?)$/;
const getTimezone = (isoString: string) => isoString.match(isoTimezoneRegex)?.shift();
const stripTimezone = (isoString: string) => isoString.replace(isoTimezoneRegex, "");

const utcToLocalTime = (date: Date | number | string) => {
  const utcDate = new Date(date);
  return new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000);
};

const unwrap = <T>(value: T | undefined | null, errorMessage?: string): T => {
  if (value === null || value === undefined) {
    throw new Error(errorMessage || "Missing value");
  } else {
    return value;
  }
};

const dateToYYMMDD = (date: Date): number => Number(format(date, "yyMMdd"));

const parseYYMMDD = (yymmddIndex: number): { year: number; month: number; day: number } => {
  const yymmdd = String(yymmddIndex)
    .match(/.{1,2}/g)
    ?.map(Number);
  if (!yymmdd || yymmdd.length !== 3) {
    throw new Error(`invalid yymmddIndex ${yymmddIndex}`);
  }
  const [year, month, day] = yymmdd;

  return {
    year: 2000 + year,
    month,
    day,
  };
};

const dateFromYYMMDD = (yymmddIndex: number): Date => {
  const { year, month, day } = parseYYMMDD(yymmddIndex);
  return new Date(Date.UTC(year, month - 1, day));
};

const stringToStream = (str: string): ReadableStream => {
  return new ReadableStream({
    start(controller) {
      controller.enqueue(new TextEncoder().encode(str));
      controller.close();
    },
  });
};

const streamToString = (stream: ReadableStream): Promise<string> => {
  const reader = stream.getReader();
  const textDecoder = new TextDecoder();
  let result = String();

  async function read(): Promise<string> {
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        result += textDecoder.decode(value, { stream: true });
      }
      result += textDecoder.decode();
      return result;
    } catch (error) {
      console.error("Stream reading error:", error);
      throw error;
    } finally {
      reader.releaseLock();
    }
  }

  return read();
};

const hexToRgb = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const applyOpacity = (baseHex: string, opacity: number): string => {
  const baseColor = hexToRgb(baseHex);

  const blendedColor = {
    r: Math.round(baseColor.r * opacity + 255 * (1 - opacity)),
    g: Math.round(baseColor.g * opacity + 255 * (1 - opacity)),
    b: Math.round(baseColor.b * opacity + 255 * (1 - opacity)),
  };

  return rgbToHex(blendedColor.r, blendedColor.g, blendedColor.b);
};

const addHashIfNeeded = (hexString: string): string =>
  hexString.startsWith("#") ? hexString : "#" + hexString;

const reorder = <T>(list: Iterable<T> | ArrayLike<T>, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const toYYMMDDListFromRange = (start: number, end: number): number[] => {
  const startDate = toUTCDatefromYYMMDDIndex(start);
  const endDate = toUTCDatefromYYMMDDIndex(end);
  return UTCDatetoYYMMDDRange(startDate, endDate).map((yymmdd) => parseInt(yymmdd));
};

const toUTCDatefromYYMMDDIndex = (yymmddIndex: number | string): Date => {
  const matchResult = (_.isString(yymmddIndex) ? yymmddIndex : String(yymmddIndex)).match(
    /.{1,2}/g,
  );
  const [yy, mm, dd] = unwrap(matchResult).map(Number);
  return new Date(Date.UTC(2000 + yy, mm - 1, dd));
};

const UTCDatetoYYMMDDRange = (start: Date, end: Date): string[] => {
  const dates: string[] = [];
  let currentDate = new Date(start);

  while (currentDate <= new Date(end)) {
    dates.push(formatUTCDateToYYMMDDIndex(currentDate));
    currentDate = new Date(
      Date.UTC(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth(),
        currentDate.getUTCDate() + 1,
      ),
    );
  }

  return dates;
};

const formatUTCDateToYYMMDDIndex = (date: Date): string => {
  const year = date.getUTCFullYear().toString().slice(-2);
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  return `${year}${month}${day}`;
};

export const emptyArrayToNull = <T>(array: T[] | null | undefined): T[] | null => {
  if (array?.length) return array;
  return null;
};

export const getLastYymmddOfYymm = (yymm: number): number => {
  const mm = yymm % 100;
  switch (mm) {
    case 1:
    case 3:
    case 5:
    case 7:
    case 8:
    case 10:
    case 12:
      return yymm * 100 + 31;
    case 2:
      return yymm * 100 + 29;
    default:
      return yymm * 100 + 30;
  }
};

export type SoftTryResult<T> =
  | {
      success: true;
      value: T;
      error: undefined;
    }
  | {
      success: false;
      value: undefined;
      error: Error;
    };

export const softTryResultUnwrap = <T>(result: SoftTryResult<T>): T => {
  if (result.success) {
    return result.value;
  } else {
    throw result.error;
  }
};

export const softTryResultMaybe = <T>(
  result: SoftTryResult<T>,
  defaultValue?: T,
): T | undefined => {
  if (result.success) {
    return result.value;
  } else {
    return defaultValue;
  }
};

export const softTry = <T>(fn: () => T): SoftTryResult<T> => {
  try {
    return {
      success: true,
      value: fn(),
      error: undefined,
    };
  } catch (error) {
    return {
      success: false,
      value: undefined,
      error: error as Error,
    };
  }
};

export const maybeSoftTry = <T>(fn: () => T, defaultValue?: T): T | undefined => {
  const result = softTry(fn);
  return softTryResultMaybe(result, defaultValue);
};

export const getTimezonedDate = (date?: Date, timeZone?: string): Date | undefined => {
  if (!date) return;
  if (!timeZone) {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return moment.tz(date, timeZone).toDate();
};

const getYymmddFromIsoAndTimezone = (inputDate?: Date, timeZone?: string): number | undefined => {
  if (!inputDate) return;
  if (!timeZone) {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  return +moment.tz(inputDate, timeZone).format("YYMMDD");
};

export const getDateDiffInDays = (date1: Date, date2: Date): number => {
  const start = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const end = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = (end.getTime() - start.getTime()) / msPerDay;

  return Math.round(diff);
};

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const formattedDate = (yymmdd: number) => {
  const { year, month, day } = t.parseYYMMDD(yymmdd);
  return `${monthNames[month - 1]} ${day}, ${year}`;
};

export const getYymmddDiffInDays = (yymmdd1: number, yymmdd2: number): number => {
  const yearMonthDay1 = parseYYMMDD(yymmdd1);
  const yearMonthDay2 = parseYYMMDD(yymmdd2);
  const start = new Date(yearMonthDay1.year, yearMonthDay1.month - 1, yearMonthDay1.day);
  const end = new Date(yearMonthDay2.year, yearMonthDay2.month - 1, yearMonthDay2.day);
  const msPerDay = 86400000;
  const diff = (end.getTime() - start.getTime()) / msPerDay;

  return Math.round(diff);
};

const formatUtcTime = (d: Date) => {
  const hour = d.getUTCHours().toString();
  const min = d.getUTCMinutes().toString();
  return `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const formatIsoToPrettyDate = (isoDateString: string, excludeWeekDay?: boolean) => {
  const date = new Date(isoDateString);
  const month = MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();
  const weekday = WEEKDAYS[date.getUTCDay()];

  return excludeWeekDay ? `${month} ${day}` : `${month} ${day} (${weekday})`;
};

export const yymmddToISOStringAndTimestamp = (num: number): [string, number] => {
  const str = String(num).padStart(6, "0");
  const yy = parseInt(str.slice(0, 2), 10);
  const mm = parseInt(str.slice(2, 4), 10);
  const dd = parseInt(str.slice(4, 6), 10);
  const fullYear = 2000 + yy;
  const date = new Date(Date.UTC(fullYear, mm - 1, dd));
  return [date.toISOString(), date.getTime()];
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${parseFloat(value.toFixed(decimals))} ${sizes[i]}`;
};

export const formatDuration = (
  milliseconds: number,
  invalidPlaceholder?: string,
): string | undefined => {
  if (!isFinite(milliseconds)) {
    return invalidPlaceholder;
  }
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) {
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (hours > 0) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  return `${seconds} second${seconds === 1 ? "" : "s"}`;
};

const formatTimestampToPrettyDate = (timestamp: number): string => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(new Date(timestamp));
  const partMap: { [key: string]: string } = {};
  parts.forEach((part) => {
    if (part.type !== "literal") partMap[part.type] = part.value;
  });

  return `${partMap.month} ${partMap.day}, ${partMap.hour}:${partMap.minute}`;
};

export const t = {
  uuid,
  log: console.log,
  utcToLocalTime,
  utcToZonedTime,
  zonedTimeToUtc,
  getTimezoneOffset,
  getTimezone,
  stripTimezone,
  unwrap,
  parseYYMMDD,
  dateToYYMMDD,
  dateFromYYMMDD,
  streamToString,
  stringToStream,
  hexToRgb,
  rgbToHex,
  applyOpacity,
  addHashIfNeeded,
  reorder,
  toYYMMDDListFromRange,
  toUTCDatefromYYMMDDIndex,
  formatUTCDateToYYMMDDIndex,
  getLastYymmddOfYymm,
  isFiniteNumber: (value: unknown | Nullish): value is number =>
    _.isNumber(value) && _.isFinite(value),
  softTry,
  maybeSoftTry,
  softTryResultUnwrap,
  softTryResultMaybe,
  noop: _.noop,
  formatOrdinals,
  getTimezonedDate,
  getYymmddFromIsoAndTimezone,
  getDateDiffInDays,
  formattedDate,
  getYymmddDiffInDays,
  formatUtcTime,
  emptyArrayToNull,
  formatIsoToPrettyDate,
  yymmddToISOStringAndTimestamp,
  formatBytes,
  formatDuration,
  formatTimestampToPrettyDate,
};
